class WxCopilotContextManager {
  constructor() {
    this.currentContext = null;
    this.pageSpecificContext = {};
    this.debounceTimer = null;
    this.retryTimer = null;
    this.mountId = 'wx-copilot-root';
    this.DEBOUNCE_DELAY = 500;
    this.RETRY_DELAY = 200;
    this.MAX_RETRIES = 10;
  }

  sendPageContext(url, mountId = 'wx-copilot-root') {
    this.mountId = mountId;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.flushPageContext(url, mountId, 0);
    }, this.DEBOUNCE_DELAY);
  }

  setWorkflowContext(workflow) {
    const changed =
      JSON.stringify(this.pageSpecificContext.workflow) !==
      JSON.stringify(workflow);
    this.pageSpecificContext.workflow = workflow;
    if (changed && this.currentContext) {
      this.sendContextUpdate();
    }
  }

  setApplicantContext(applicant) {
    const changed =
      JSON.stringify(this.pageSpecificContext.applicant) !==
      JSON.stringify(applicant);
    this.pageSpecificContext.applicant = applicant;
    if (changed && this.currentContext) {
      this.sendContextUpdate();
    }
  }

  setApplicantTableContext(applicantTable) {
    const changed =
      JSON.stringify(this.pageSpecificContext.applicantTable) !==
      JSON.stringify(applicantTable);
    this.pageSpecificContext.applicantTable = applicantTable;
    if (changed && this.currentContext) {
      this.sendContextUpdate();
    }
  }

  sendContextUpdate() {
    if (!this.currentContext) return;

    const fullContext = {
      ...this.currentContext,
      ...this.pageSpecificContext,
      timestamp: Date.now(),
    };

    this.currentContext = fullContext;
    window.wxCopilotPageContext = fullContext;
    this.pushToCopilot(fullContext, 0);
  }

  flushPageContext(url, mountId, retryCount = 0) {
    const newContext = {
      url,
      title: typeof document !== 'undefined' ? document.title : undefined,
      timestamp: Date.now(),
      ...this.pageSpecificContext,
    };

    if (this.hasContextChanged(newContext)) {
      this.currentContext = newContext;
      window.wxCopilotPageContext = newContext;
    }

    this.pushToCopilot(newContext, retryCount);
  }

  pushToCopilot(context, retryCount = 0) {
    try {
      const copilotInstance =
        window.WxCopilotComponentInstances?.[this.mountId];

      if (copilotInstance?.updateEphemeralContext) {
        const contextPayload = {
          page: {
            url: context.url,
            title: context.title,
            timestamp: context.timestamp,
          },
        };

        if (context.workflow) {
          contextPayload.workflow = {
            id: context.workflow.workflowId,
            name: context.workflow.workflowName,
            currentStageId: context.workflow.currentStageId,
            currentStageName: context.workflow.currentStageName,
          };
        }

        if (context.applicant) {
          contextPayload.applicant = {
            id: context.applicant.applicantId,
            name: context.applicant.applicantName,
            email: context.applicant.applicantEmail,
            stage: context.applicant.stageId
              ? {
                  id: context.applicant.stageId,
                  title: context.applicant.stageName,
                }
              : undefined,
            opening: context.applicant.openingId
              ? {
                  id: context.applicant.openingId,
                  name: context.applicant.openingName,
                }
              : undefined,
          };
        }

        if (context.applicantTable) {
          contextPayload.applicantTable = {
            opening: {
              id: context.applicantTable.openingId,
              name: context.applicantTable.openingName,
            },
            currentStage: context.applicantTable.currentStageId
              ? {
                  id: context.applicantTable.currentStageId,
                  title: context.applicantTable.currentStageName,
                  type: context.applicantTable.currentStageType,
                }
              : undefined,
            stages: context.applicantTable.stages,
            applicants: context.applicantTable.applicants,
            totalApplicants: context.applicantTable.totalApplicants,
          };
        }

        copilotInstance.updateEphemeralContext(JSON.stringify(contextPayload));
      } else if (retryCount < this.MAX_RETRIES) {
        if (this.retryTimer) {
          clearTimeout(this.retryTimer);
        }
        this.retryTimer = setTimeout(() => {
          this.pushToCopilot(context, retryCount + 1);
        }, this.RETRY_DELAY);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[WxCopilot] Error updating page context:', error);
    }
  }

  hasContextChanged(newContext) {
    if (!this.currentContext) {
      return true;
    }

    return (
      this.currentContext.url !== newContext.url ||
      JSON.stringify(this.currentContext.workflow) !==
        JSON.stringify(newContext.workflow) ||
      JSON.stringify(this.currentContext.applicant) !==
        JSON.stringify(newContext.applicant) ||
      JSON.stringify(this.currentContext.applicantTable) !==
        JSON.stringify(newContext.applicantTable)
    );
  }

  clearPageSpecificContext() {
    this.pageSpecificContext = {};
  }

  cleanup() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }
}

export const wxCopilotContextManager = new WxCopilotContextManager();
